<template>
  <perfect-scrollbar>
    <CollapsibleList :items="groupedBots"
                     v-model="openBots"
                     :selected-index="selectedGroupIndex"
                     :is-selected="['bot', 'addMarket'].includes(selectedPanelLevel) && selectedGroupIndex >= 0 && !isRenameMode && !selectedChannel"
    >
      <template #title="{ item }">
        <div class="flex justify-between items-center w-full cursor-pointer"
             @click="handleGroupSelect(item)"
        >
          <div class="flex items-center gap-3 h-5">
            <span class="inline-flex items-center h-5">{{ item.name }}</span>
            <vx-tooltip :text="badgeTooltip(item)" class="inline-flex items-center h-5">
              <span class="count-badge">{{ item.badgeCount }}</span>
            </vx-tooltip>
          </div>

          <div class="flex items-center gap-1">
            <button v-if="item.hasMultiBot"
                    class="bg-transparent border-0 cursor-pointer p-3 rounded-lg hover:bg-primary-light flex items-center gap-1"
                    :class="activeAddMarketGroupId === item.id ? 'text-focus-error' : 'text-gray-dark'"
                    @click.stop="$emit('request-add-market', item)"
            >
              <template v-if="activeAddMarketGroupId === item.id">
                <span>Cancel</span>
                <CloseIcon class="h-4 w-4"/>
              </template>
              <template v-else>
                + Add market
              </template>
            </button>
            <div @click.prevent.stop class="w-12">
              <side-list-menu :items="resolveMenuItems(item)"
                              @rename="$emit('request-rename', item.primaryBot)"
                              @delete="$emit('request-delete', item.primaryBot)"
              />
            </div>
          </div>
        </div>
      </template>

      <template #content="{ item }">
        <CollapsibleList v-if="item.hasMultiBot && item.markets.length > 0"
                         class="nested-collapsible-list gap-0"
                         :items="item.markets"
                         :value="openMarkets[item.id] || {}"
                         @input="setMarketOpenState(item.id, $event)"
                         :selected-index="selectedMarketIndex(item)"
                         :is-selected="['market', 'addChannel'].includes(selectedPanelLevel) && selectedMarketIndex(item) >= 0 && !selectedChannel"
        >
          <template #title="{ item: market }">
            <div class="flex flex-1 min-w-0 justify-between items-center w-full cursor-pointer"
                :class="{ 'bg-primary-light': ['market', 'addChannel'].includes(selectedPanelLevel) && selectedBot && selectedBot.id === market.primaryBot.id && !selectedChannel }"
                 @mouseenter="hoveredMarketId = market.primaryBot.id"
                 @mouseleave="hoveredMarketId = null"
                 @focusin="focusedMarketId = market.primaryBot.id"
                 @focusout="focusedMarketId = null"
                 @click="selectMarket(market)"
            >
              <span class="min-w-0 truncate">{{ market.name }}</span>
              <div class="flex items-center gap-1 flex-shrink-0">
                <button type="button"
                        class="bg-transparent border-0 cursor-pointer p-3 rounded-lg hover:bg-primary-light flex items-center gap-1"
                        :class="activeAddChannelMarketId === market.primaryBot.id ? 'text-focus-error' : 'text-gray-dark'"
                        :style="showAddChannelButton(market) ? null : { visibility: 'hidden', pointerEvents: 'none' }"
                        @click.stop="$emit('request-add-channel', market.primaryBot)"
                >
                  <template v-if="activeAddChannelMarketId === market.primaryBot.id">
                    <span>Cancel</span>
                    <CloseIcon class="h-4 w-4"/>
                  </template>
                  <template v-else>
                    + Add channel
                  </template>
                </button>
                <div @click.prevent.stop
                     class="w-12 flex-shrink-0 flex items-center justify-end"
                >
                  <div :class="{ 'invisible pointer-events-none': !showMarketMenu(market) }"
                       class="flex items-center justify-end pr-2"
                  >
                    <side-list-menu :items="resolveMarketMenuItems(market)"
                                    @rename="$emit('request-rename-market', market.primaryBot)"
                                    @delete="$emit('request-delete-market', market.primaryBot)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template #content="{ item: market }">
            <SettingsBotsListChannel v-for="(channel, i) in market.primaryBot.channels"
                                     :key="channel.id"
                                     :channel="channel"
                                     :selected-channel="selectedChannel"
                                     :is-last="i === market.primaryBot.channels.length - 1"
                                     class="pl-18"
                                     @select-channel="selectChannel(channel, market.primaryBot)"
            />
          </template>
        </CollapsibleList>

        <div v-else-if="item.hasMultiBot">
          <SettingsBotsListChannel v-for="(channel, i) in item.primaryBot.channels"
                                   :key="channel.id"
                                   :channel="channel"
                                   :selected-channel="selectedChannel"
                                   :is-last="i === item.primaryBot.channels.length - 1"
                                   @select-channel="selectChannel(channel, item.primaryBot)"
          />
        </div>

        <div v-else>
          <SettingsBotsListChannel v-for="(channel, i) in item.primaryBot.channels"
                                   :key="channel.id"
                                   :channel="channel"
                                   :selected-channel="selectedChannel"
                                   :is-last="i === item.primaryBot.channels.length - 1"
                                   @select-channel="selectChannel(channel, item.primaryBot)"
          />
        </div>
      </template>
    </CollapsibleList>
  </perfect-scrollbar>
</template>

<script>
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import CollapsibleList from '@/components/common/CollapsibleList.vue'
import SettingsBotsListChannel from '@/components/settings/bots/SettingsBotsListChannel.vue'
import SideListMenu from '@/components/common/SideListMenu.vue'
import CloseIcon from '@/assets/images/icons/CloseIcon.vue'
import { ROUTE_NAMES } from '@/router'
import { MENU_ITEMS } from '@/utils/editor.constants'

export default {
  name: 'SettingsBotsList',

  components: {
    CloseIcon,
    SideListMenu,
    SettingsBotsListChannel,
    CollapsibleList,
    PerfectScrollbar
  },

  props: {
    selectedBotIndex: {
      type: Number,
      default: 0
    },
    selectedPanelLevel: {
      type: String,
      default: 'bot'
    },
    bots: {
      type: Array,
      required: true
    },
    multiBotsById: {
      type: Object,
      default: () => ({})
    },
    activeAddMarketGroupId: {
      type: String,
      default: null
    },
    activeAddChannelMarketId: {
      type: String,
      default: null
    },
    isRenameMode: {
      type: Boolean,
      default: false
    },
    selectedChannel: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      openBots: {},
      openMarkets: {},
      hoveredMarketId: null,
      focusedMarketId: null
    }
  },

  emits: ['request-rename', 'update:bot-name', 'request-delete', 'request-add-market', 'request-add-channel', 'request-rename-market', 'request-delete-market', 'select-channel-index', 'select-bot-index', 'select-market-bot-index'],

  computed: {
    selectedBot() {
      return this.bots[this.selectedBotIndex]
    },
    groupedBots() {
      const groups = []
      const allMultiBots = Object.values(this.multiBotsById || {})
      const normalizePrimaryBotId = mb => mb?.primary_bot_id || mb?.primaryBotId || null
      const botById = this.bots.reduce((acc, bot) => {
        acc[bot.id] = bot
        return acc
      }, {})
      const primaryBots = this.bots.filter(bot => bot?.is_primary_variant)

      primaryBots.forEach(primaryBot => {
        const rootMultiBotId = primaryBot.multi_bot_id
        const rootMultiBot = rootMultiBotId ? this.multiBotsById[rootMultiBotId] : null

        if (!rootMultiBot) {
          groups.push({
            id: primaryBot.id,
            name: primaryBot.name,
            hasMultiBot: false,
            markets: [primaryBot],
            primaryBot,
            badgeCount: primaryBot.channels?.length || 0
          })
          return
        }

        const siblingBots = this.bots.filter(bot =>
          bot.id === primaryBot.id || bot.multi_bot_id === rootMultiBotId
        )
        const siblingBotIds = new Set(siblingBots.map(bot => bot.id))

        const marketMultiBots = allMultiBots.filter(multiBot => {
          const marketPrimaryBotId = normalizePrimaryBotId(multiBot)
          return marketPrimaryBotId && siblingBotIds.has(marketPrimaryBotId)
        })

        const markets = marketMultiBots.map(multiBot => {
          const marketPrimaryBotId = normalizePrimaryBotId(multiBot)
          const marketPrimaryBot = botById[marketPrimaryBotId]
          if (!marketPrimaryBot) return null

          return {
            name: multiBot.name,
            multiBotName: multiBot.name,
            marketMultiBotId: multiBot.id,
            primaryBotId: marketPrimaryBotId,
            primaryBot: marketPrimaryBot,
            isPrimaryMarket: multiBot.id === rootMultiBotId
          }
        })
          .filter(Boolean)
          .sort((a, b) => {
            if (a.isPrimaryMarket) return -1
            if (b.isPrimaryMarket) return 1
            return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
          })

        const primaryBotWithMarkets = { ...primaryBot, markets }

        groups.push({
          id: rootMultiBotId,
          name: primaryBot.name,
          hasMultiBot: true,
          markets,
          primaryBot: primaryBotWithMarkets,
          badgeCount: markets.length > 0 ? markets.length : primaryBot.channels?.length || 0
        })
      })

      const groupedPrimaryIds = new Set(primaryBots.map(bot => bot.id))
      const standaloneBots = this.bots.filter(bot =>
        !bot.is_primary_variant &&
        !bot.multi_bot_id &&
        !groupedPrimaryIds.has(bot.id)
      )

      standaloneBots.forEach(bot => {
        groups.push({
          id: bot.id,
          name: bot.name,
          hasMultiBot: false,
          markets: [bot],
          primaryBot: bot,
          badgeCount: bot.channels?.length || 0
        })
      })

      return groups
    },
    selectedGroupIndex() {
      if (this.selectedBotIndex < 0) return -1

      const selectedBot = this.bots[this.selectedBotIndex]
      if (!selectedBot) return -1

      return this.groupedBots.findIndex(group => {
        if (group.primaryBot?.id === selectedBot?.id) return true

        return group.markets.some(market => market.primaryBot?.id === selectedBot?.id)
      })
    }
  },

  watch: {
    selectedBotIndex: {
      immediate: true,
      handler(newIndex) {
        if (newIndex < 0) return

        const selectedBot = this.bots[newIndex]
        const selectedGroupIndex = this.groupedBots.findIndex(group => {
          if (group.primaryBot?.id === selectedBot?.id) return true

          return group.markets.some(market => market.primaryBot?.id === selectedBot?.id)
        })
        if (selectedGroupIndex < 0) return

        const selectedGroup = this.groupedBots[selectedGroupIndex]
        this.$set(this.openBots, selectedGroupIndex, true)
        if (!selectedGroup.hasMultiBot || !selectedGroup.markets.length) return

        const marketOpenItems = this.openMarkets[selectedGroup.id] || {}
        const selectedMarketIndex = selectedGroup.markets.findIndex(market => market.primaryBot.id === selectedBot.id)
        if (selectedMarketIndex < 0) return

        this.$set(marketOpenItems, selectedMarketIndex, true)
        this.$set(this.openMarkets, selectedGroup.id, marketOpenItems)
      }
    }
  },

  methods: {
    resolveMenuItems(group) {
      return ['111', '222'].includes(group.primaryBot.id) ? [MENU_ITEMS.rename] : [MENU_ITEMS.rename, MENU_ITEMS.delete]
    },
    resolveMarketMenuItems(market) {
      return ['111', '222'].includes(market.primaryBot.id) ? [MENU_ITEMS.rename] : [MENU_ITEMS.rename, MENU_ITEMS.delete]
    },
    showMarketMenu(market) {
      const marketPrimaryBotId = market.primaryBot.id
      const isSelected = ['market', 'addChannel'].includes(this.selectedPanelLevel) && this.selectedBot && this.selectedBot.id === marketPrimaryBotId && !this.selectedChannel
      return isSelected || this.hoveredMarketId === marketPrimaryBotId
    },
    showAddChannelButton(market) {
      const marketPrimaryBotId = market.primaryBot.id
      const isSelected = ['market', 'addChannel'].includes(this.selectedPanelLevel) && this.selectedBot && this.selectedBot.id === marketPrimaryBotId && !this.selectedChannel
      return isSelected || this.hoveredMarketId === marketPrimaryBotId || this.focusedMarketId === marketPrimaryBotId
    },
    badgeTooltip(group) {
      const count = group.badgeCount || 0
      if (group.hasMultiBot && group.markets.length > 0) {
        return `${count} ${count === 1 ? 'market' : 'markets'}`
      }
      if (group.hasMultiBot) {
        return `${count} ${count === 1 ? 'channel' : 'channels'}`
      }
      return `${count} ${count === 1 ? 'channel' : 'channels'}`
    },
    setMarketOpenState(groupId, openState) {
      this.$set(this.openMarkets, groupId, openState)
    },
    selectedMarketIndex(group) {
      if (!['market', 'addChannel'].includes(this.selectedPanelLevel)) return -1
      if (!this.selectedBot) return -1
      return group.markets.findIndex(market => market.primaryBot.id === this.selectedBot.id)
    },
    getSelectedBotIndex(botId) {
      return this.bots.findIndex(bot => bot.id === botId)
    },
    selectChannel(channel, bot) {
      if (channel.id === this.selectedChannel?.id) return

      const selectedBotIndex = this.getSelectedBotIndex(bot.id)
      if (selectedBotIndex < 0) return

      const selectedBot = this.bots[selectedBotIndex]
      const channelIndex = selectedBot.channels.findIndex(c => c.id === channel.id)
      const selectedGroupIndex = this.groupedBots.findIndex(group => {
        if (group.primaryBot?.id === selectedBot.id) return true
        return group.markets?.length && group.markets.some(market => market.primaryBot?.id || market.id === selectedBot.id)
      })
      if (selectedGroupIndex >= 0) this.$set(this.openBots, selectedGroupIndex, true)
      this.$emit('select-bot-index', selectedBotIndex)
      this.$emit('select-channel-index', channelIndex)
      this.$router.push({
        name: ROUTE_NAMES.botsWithChannel,
        params: {
          botId: selectedBot.id,
          channelId: channel.id
        }
      })
    },
    selectMarket(market) {
      const marketBotId = market?.primaryBot?.id
      if (!marketBotId) return

      const selectedBotIndex = this.getSelectedBotIndex(marketBotId)
      if (selectedBotIndex < 0) return

      this.$emit('select-market-bot-index', selectedBotIndex)
    },
    handleGroupSelect(group) {
      const selectedBotIndex = this.getSelectedBotIndex(group.primaryBot.id)
      if (selectedBotIndex < 0) return

      const selectedGroupIndex = this.groupedBots.findIndex(g => g.id === group.id)
      this.$emit('select-bot-index', selectedBotIndex)
      if (selectedGroupIndex >= 0) this.$set(this.openBots, selectedGroupIndex, true)
      this.$router.push({
        name: ROUTE_NAMES.bots,
        params: {
          botId: group.primaryBot.id
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.count-badge {
  @apply h-5 min-w-5 px-1 rounded-full inline-flex items-center justify-center bg-white border border-solid border-gray-border text-xs text-gray-dark;
  line-height: 1;
}

.nested-collapsible-list ::v-deep .collapsible-list-item {
  @apply border-0 border-b border-solid border-soft rounded-none;
}

.nested-collapsible-list ::v-deep .collapsible-list-item:last-child {
  border-bottom: 0;
}

.nested-collapsible-list ::v-deep .collapsible-list-item > .w-full {
  @apply pl-6 py-3;
  align-items: center;
}

.nested-collapsible-list ::v-deep .collapsible-list-item-content {
  border-top: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.nested-collapsible-list ::v-deep .collapsible-list-item.closed .arrow-container-icon {
  transform: rotate(90deg) !important;
}

.nested-collapsible-list ::v-deep .collapsible-list-item.open .arrow-container-icon {
  transform: rotate(180deg) !important;
}
</style>

<style lang="scss">
#settings-bots-table {
  table {
    display: inline-block;

    tbody {
      max-height: 100% !important;

      & > div {
        max-height: 70vh !important;
      }

      .table-body-wrapper {
        height: 100% !important;
      }
    }
  }
}
</style>
